
import { computed, defineComponent, onErrorCaptured, onMounted, provide, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useForm } from 'vee-validate';

import { CustomersAppRoutesEnum } from '../../router/CustomersAppRoutesEnum';
import { Error422 } from '@/model/common/Error422';
import { Customer } from '@/model/Customer';
import { useMessage } from '@plugins/Messages-plugin';
import { customersAppService } from '@services/customers-app.service';

import { PageHeader } from '@/components';
import CustomerAppData from './CustomerAppData/CustomerAppData.vue';

export default defineComponent({
  name: 'CustomerAppPage',

  components: {
    PageHeader,
    CustomerAppData,
  },

  props: ['customerId'],

  setup(props) {
    const isLoading = ref(false);

    const { confirmMessage, successMessage, errorMessage } = useMessage();

    const { t: $t } = useI18n();

    const router = useRouter();

    const loading = ref(false);
    provide('loading', loading);

    const backRoute = { name: CustomersAppRoutesEnum.CUSTOMERS_APP_LIST };

    const isNew = computed(() => props.customerId === 'new');

    const customer = ref<Customer>();

    // L'unico user associato all'entity
    const customerUser = computed(() => customer.value?.users?.[0] || null);

    // Verifica se è trasportatore
    const isTransporter = computed(() => !!customer.value?.community_transporter);

    // Schema dinamico: campi trasportatore obbligatori solo se community_transporter
    const validationSchema = computed(() => {
      const baseSchema = {
        // Dati utente (sempre obbligatori)
        name: 'required',
        surname: 'required',
        email: 'required|email',
        phone: null,
        is_active: null,
        community_transporter: null,
        // Password (opzionale)
        password: null,
        confirmPassword: null,
      };

      // Campi trasportatore (obbligatori solo se community_transporter)
      const transporterSchema = isTransporter.value ? {
        business_name: 'required',
        business_city_name: 'required',
        business_city_id: null,
        business_address: 'required',
        business_province: null,
        business_postal_code: 'required',
        fiscal_code: 'required',
        iban_holder: 'required',
        iban: 'required',
      } : {
        business_name: null,
        business_city_name: null,
        business_city_id: null,
        business_address: null,
        business_province: null,
        business_postal_code: null,
        fiscal_code: null,
        iban_holder: null,
        iban: null,
      };

      return { ...baseSchema, ...transporterSchema };
    });

    const { values, setValues, validate, setFieldTouched } = useForm({ validationSchema });

    const onSubmit = async () => {
      const { valid } = await validate();
      if (!valid) {
        Object.keys(values).forEach((key) => setFieldTouched(key, true));
        errorMessage($t('common.required_fields'));
        return;
      }

      // Verifica password se valorizzata
      const { password, confirmPassword } = values as any;
      if (password && password.length > 0) {
        if (password.length < 6) {
          errorMessage('La password deve avere almeno 6 caratteri');
          return;
        }
        if (password !== confirmPassword) {
          errorMessage('Le password non coincidono');
          return;
        }
      }

      // Prepara il payload unico con tutti i campi
      const payload: any = {
        is_active: values.is_active,
        community_transporter: values.community_transporter,
        name: values.name,
        surname: values.surname,
        email: values.email,
        phone: values.phone || undefined,
      };

      // Aggiungi campi trasportatore se presenti
      if (isTransporter.value) {
        payload.business_name = values.business_name;
        payload.fiscal_code = values.fiscal_code;
        payload.business_address = values.business_address;
        payload.business_phone = values.phone;
        payload.business_city_id = values.business_city_id;
        payload.business_city_name = values.business_city_name;
        payload.business_province = values.business_province;
        payload.business_postal_code = values.business_postal_code;
        payload.business_tel = values.phone;
        payload.iban_holder = values.iban_holder;
        payload.iban = values.iban;
      }

      // Aggiungi password solo se valorizzata
      if (password && password.length > 0) {
        payload.password = password;
      }

      console.log('Payload inviato:', JSON.stringify(payload, null, 2));

      let response;

      if (customer.value?.id) {
        // UPDATE - invia il payload completo
        payload.id = customer.value.id;
        response = await customersAppService.updatePatch(payload);
      } else {
        // CREATE
        response = await customersAppService.create(payload);

        router.replace({
          name: CustomersAppRoutesEnum.CUSTOMERS_APP_DETAIL,
          params: { customerId: response.id },
        });
      }

      await loadCustomer();

      successMessage($t('message.success_message'));
    };

    function onDelete() {
      const { name, surname } = customer.value as Customer;

      confirmMessage(
          `Confermare la cancellazione del cliente: ${name} ${surname} ?`,
          'Cancellazione cliente'
      ).then(async (response) => {
        if (response) {
          await customersAppService.remove(customer.value as Customer);

          router
              .replace({ name: CustomersAppRoutesEnum.CUSTOMERS_APP_LIST })
              .then(() => {
                successMessage($t('message.success_message'));
              });
        }
      });
    }

    async function loadCustomer() {
      try {
        customer.value = await customersAppService.getById(+props.customerId);
      } catch (error) {
        errorMessage('Caricamento cliente non riuscito');
      } finally {
        isLoading.value = false;
      }
    }

    function initFormValues() {
      const user = customer.value?.users?.[0];
      setValues({
        ...customer.value,
        // Sovrascrivi con i dati dell'user se presente
        name: user?.name || customer.value?.name,
        surname: user?.surname || customer.value?.surname,
        email: user?.email || customer.value?.email,
        phone: (user as any)?.phone || '',
      } as any);
    }

    function handle422({ errors }: Error422) {
      const fieldsInError = Object.keys(errors || {});

      function flattenString(a: string[]) {
        return a?.join(' ');
      }

      if (fieldsInError?.length) {
        fieldsInError.forEach((k) => {
          errorMessage(flattenString(errors[k]));
        });
      }
    }

    onErrorCaptured((e: any) => {
      if (e.status === 422) {
        handle422(e.data);
        return false;
      }
    });

    onMounted(async () => {
      isLoading.value = true;

      if (isNew.value) {
        customer.value = new Customer();
        isLoading.value = false;
      } else {
        await loadCustomer();
      }

      initFormValues();
    });

    return {
      customer,
      customerUser,
      loadCustomer,
      backRoute,
      isNew,
      isLoading,
      onSubmit,
      onDelete,
    };
  },
});
